import { BaseApiClient } from './base-client'
import {
  API_ROUTES,
  type BillingConfigResponse,
  type CreateSetupIntentResponse,
  type CreateSubscriptionDto,
  type InvoiceHistoryItem,
  type SubscriptionResponse,
  type UpdatePaymentMethodDto,
  type UsageSnapshotResponse,
} from '@super/shared-types'

interface Envelope<T> {
  data: T
}

class BillingApiClient extends BaseApiClient {
  async getConfig(): Promise<BillingConfigResponse> {
    const response = await this.request<Envelope<BillingConfigResponse>>(
      API_ROUTES.BILLING.GET_CONFIG,
      { method: 'GET', credentials: 'include' },
    )
    return response.data
  }

  async createSetupIntent(): Promise<CreateSetupIntentResponse> {
    const response = await this.request<Envelope<CreateSetupIntentResponse>>(
      API_ROUTES.BILLING.CREATE_SETUP_INTENT,
      { method: 'POST', credentials: 'include' },
    )
    return response.data
  }

  async createSubscription(
    dto: CreateSubscriptionDto,
  ): Promise<SubscriptionResponse> {
    const response = await this.request<Envelope<SubscriptionResponse>>(
      API_ROUTES.BILLING.CREATE_SUBSCRIPTION,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto),
      },
    )
    return response.data
  }

  async getSubscription(): Promise<SubscriptionResponse | null> {
    const response = await this.request<Envelope<SubscriptionResponse | null>>(
      API_ROUTES.BILLING.GET_SUBSCRIPTION,
      { method: 'GET', credentials: 'include' },
    )
    return response.data
  }

  async updatePaymentMethod(
    dto: UpdatePaymentMethodDto,
  ): Promise<SubscriptionResponse> {
    const response = await this.request<Envelope<SubscriptionResponse>>(
      API_ROUTES.BILLING.UPDATE_PAYMENT_METHOD,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto),
      },
    )
    return response.data
  }

  async cancelSubscription(): Promise<SubscriptionResponse> {
    const response = await this.request<Envelope<SubscriptionResponse>>(
      API_ROUTES.BILLING.CANCEL_SUBSCRIPTION,
      { method: 'DELETE', credentials: 'include' },
    )
    return response.data
  }

  async reactivateSubscription(): Promise<SubscriptionResponse> {
    const response = await this.request<Envelope<SubscriptionResponse>>(
      API_ROUTES.BILLING.REACTIVATE_SUBSCRIPTION,
      { method: 'POST', credentials: 'include' },
    )
    return response.data
  }

  async getInvoices(): Promise<InvoiceHistoryItem[]> {
    const response = await this.request<Envelope<InvoiceHistoryItem[]>>(
      API_ROUTES.BILLING.LIST_INVOICES,
      { method: 'GET', credentials: 'include' },
    )
    return response.data
  }

  async getUsage(): Promise<UsageSnapshotResponse | null> {
    const response = await this.request<Envelope<UsageSnapshotResponse | null>>(
      API_ROUTES.BILLING.GET_USAGE,
      { method: 'GET', credentials: 'include' },
    )
    return response.data
  }
}

export const billingClient = new BillingApiClient()
